/* eslint-disable max-lines -- Keep CAPTCHA lifecycle guards in one provider to prevent divergent implementations. */

import { CaptchaType, RecaptchaEnterpriseMode, Theme } from '@logto/schemas';
import { useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useToast from '@/hooks/use-toast';
import { type SignInExperienceResponse } from '@/types';

import PageContext from '../PageContextProvider/PageContext';

import CaptchaContext, { type CaptchaContextType } from './CaptchaContext';
import { aliyunCaptchaButtonId, aliyunCaptchaElementId, scriptId } from './constant';
import { getScript } from './utils';

const aliyunCaptchaInitTimeout = 15_000;
const aliyunCaptchaAttemptTimeout = 120_000;
// Aliyun delays success by 1s when delayBeforeSuccess=true. Leave enough room
// for that callback to arrive after the popup's verifyComplete close event.
const aliyunCaptchaCloseGracePeriod = 2000;

type AliyunCaptchaInstance = {
  show?: () => void | Promise<void>;
  destroyCaptcha?: () => void;
  config?: {
    CaptchaType?: string;
    captchaType?: string;
  };
};

type AliyunCaptchaExecution = {
  cancel: () => void;
};

const safelyDestroyAliyunCaptcha = (instance?: AliyunCaptchaInstance) => {
  try {
    instance?.destroyCaptcha?.();
  } catch {
    // SDK cleanup is best-effort and must not break a subsequent auth flow.
  }
};

export type CaptchaRuntime = {
  captchaPolicy?: SignInExperienceResponse['captchaPolicy'];
  captchaConfig?: SignInExperienceResponse['captchaConfig'];
  theme: Theme;
  setToast: (message: string) => void;
  language: string;
};

type Props = {
  readonly children: React.ReactNode;
  /**
   * Account Center consumes the same public CAPTCHA configuration as Experience
   * but owns a distinct page context. Passing this runtime keeps the provider
   * implementation and Aliyun lifecycle identical across both surfaces.
   */
  readonly runtime?: CaptchaRuntime;
};

const CaptchaContextProvider = ({ children, runtime }: Props) => {
  const { experienceSettings, theme: experienceTheme } = useContext(PageContext);
  const widgetRef = useRef<HTMLDivElement>(null);
  const aliyunInstanceRef = useRef<AliyunCaptchaInstance>();
  const aliyunExecutionRef = useRef<AliyunCaptchaExecution>();
  const { setToast: setExperienceToast } = useToast();
  const { t, i18n } = useTranslation();

  const captchaPolicy = runtime?.captchaPolicy ?? experienceSettings?.captchaPolicy;
  const captchaConfig = runtime?.captchaConfig ?? experienceSettings?.captchaConfig;
  const theme = runtime?.theme ?? experienceTheme;
  const setToast = runtime?.setToast ?? setExperienceToast;
  const language = runtime?.language ?? i18n.language;

  const isCaptchaRequired = Boolean(captchaPolicy?.enabled);

  const destroyCurrentAliyunCaptcha = useCallback(() => {
    const instance = aliyunInstanceRef.current;
    // Clear the ref before invoking third-party cleanup so stale callbacks cannot reuse it.
    // eslint-disable-next-line @silverhand/fp/no-mutation -- ref lifecycle is local to this provider.
    aliyunInstanceRef.current = undefined;
    safelyDestroyAliyunCaptcha(instance);
  }, []);

  const initCaptcha = useCallback(() => {
    if (!isCaptchaRequired || !captchaConfig) {
      return;
    }

    if (document.querySelector(`#${scriptId}`)) {
      return;
    }

    const script = document.createElement('script');

    if (captchaConfig.type === CaptchaType.Aliyun) {
      // The SDK reads this global during script evaluation. The same values are also passed to
      // initAliyunCaptcha below so a runtime provider change stays deterministic.
      // eslint-disable-next-line @silverhand/fp/no-mutation -- Aliyun SDK requires global bootstrap configuration.
      window.AliyunCaptchaConfig = {
        region: captchaConfig.region,
        prefix: captchaConfig.prefix,
      };
    }

    /* eslint-disable @silverhand/fp/no-mutation */
    script.src = getScript(captchaConfig);
    script.id = scriptId;
    script.async = true;
    /* eslint-enable @silverhand/fp/no-mutation */

    document.body.append(script);
  }, [isCaptchaRequired, captchaConfig]);

  const executeCaptcha = useCallback(async () => {
    if (!isCaptchaRequired) {
      return;
    }

    if (!captchaConfig) {
      setToast(t('error.captcha_verification_failed'));
      throw new Error('Captcha configuration is unavailable');
    }

    if (captchaConfig.type === CaptchaType.Turnstile) {
      return new Promise<string | undefined>((resolve, reject) => {
        if (!window.turnstile || !widgetRef.current) {
          setToast(t('error.captcha_verification_failed'));
          reject(new Error('Turnstile is unavailable'));
          return;
        }

        // Clear the dom element first
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widgetRef.current.innerHTML = '';

        window.turnstile.render(widgetRef.current, {
          sitekey: captchaConfig.siteKey,
          theme: theme === Theme.Light ? 'light' : 'dark',
          callback: (token: string) => {
            if (typeof token !== 'string' || token.trim().length === 0) {
              setToast(t('error.captcha_verification_failed'));
              reject(new Error('Turnstile returned an empty token'));
              return;
            }

            resolve(token);
          },
          'error-callback': (errorCode) => {
            setToast(t('error.captcha_verification_failed'));
            reject(new Error(`Turnstile error: ${errorCode}`));
          },
          size: 'flexible',
        });
      });
    }

    if (captchaConfig.type === CaptchaType.Aliyun) {
      return new Promise<string>((resolve, reject) => {
        const { initAliyunCaptcha } = window;
        const captchaElement = widgetRef.current;
        const captchaButton = document.querySelector<HTMLButtonElement>(
          `#${aliyunCaptchaButtonId}`
        );

        if (!initAliyunCaptcha || !captchaElement || !captchaButton) {
          setToast(t('error.captcha_verification_failed'));
          reject(new Error('Aliyun CAPTCHA is unavailable'));
          return;
        }

        if (aliyunExecutionRef.current) {
          setToast(t('error.captcha_verification_failed'));
          reject(new Error('Aliyun CAPTCHA execution is already in progress'));
          return;
        }

        const execution: {
          settled: boolean;
          instanceCreated: boolean;
          showing: boolean;
          popupShown: boolean;
          pendingSuccess?: unknown;
          pendingSuccessReceived: boolean;
          closeTimer?: number;
        } = {
          settled: false,
          instanceCreated: false,
          showing: false,
          popupShown: false,
          pendingSuccessReceived: false,
        };
        const initTimer = window.setTimeout(() => {
          fail(new Error('Aliyun CAPTCHA initialization timed out'));
        }, aliyunCaptchaInitTimeout);
        const attemptTimer = window.setTimeout(() => {
          fail(new Error('Aliyun CAPTCHA attempt timed out'));
        }, aliyunCaptchaAttemptTimeout);

        const cleanup = () => {
          window.clearTimeout(initTimer);
          window.clearTimeout(attemptTimer);

          if (execution.closeTimer !== undefined) {
            window.clearTimeout(execution.closeTimer);
          }

          // eslint-disable-next-line @silverhand/fp/no-mutation
          aliyunExecutionRef.current = undefined;
        };

        const succeed = (captchaVerifyParam: unknown) => {
          if (execution.settled) {
            return;
          }

          // Aliyun can invoke `success` from its disaster-recovery path before an
          // interactive instance exists. Never turn that callback into a billable
          // SMS request; a valid proof requires both getInstance and show.
          if (!execution.instanceCreated || !execution.popupShown) {
            if (execution.instanceCreated && execution.showing) {
              // Some SDK builds call success synchronously from show(). Hold it
              // until show returns successfully, then run the same validation.
              // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
              execution.pendingSuccess = captchaVerifyParam;
              // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
              execution.pendingSuccessReceived = true;
              return;
            }

            fail(new Error('Aliyun CAPTCHA succeeded without an interactive popup'));
            return;
          }

          if (typeof captchaVerifyParam !== 'string' || captchaVerifyParam.trim().length === 0) {
            fail(new Error('Aliyun CAPTCHA returned an empty token'));
            return;
          }

          // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
          execution.settled = true;
          cleanup();
          resolve(captchaVerifyParam);
        };

        function fail(error: Error, showToast = true) {
          if (execution.settled) {
            return;
          }

          // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
          execution.settled = true;
          cleanup();

          if (showToast) {
            setToast(t('error.captcha_verification_failed'));
          }

          reject(error);
        }

        // Every execution creates a fresh SDK instance. A returned parameter is never reused.
        destroyCurrentAliyunCaptcha();
        // eslint-disable-next-line @silverhand/fp/no-mutation
        captchaElement.innerHTML = '';
        // eslint-disable-next-line @silverhand/fp/no-mutation -- Aliyun SDK reads this before each initialization.
        window.AliyunCaptchaConfig = {
          region: captchaConfig.region,
          prefix: captchaConfig.prefix,
        };
        // eslint-disable-next-line @silverhand/fp/no-mutation
        aliyunExecutionRef.current = {
          cancel: () => {
            fail(new Error('Aliyun CAPTCHA execution was cancelled'), false);
          },
        };

        const handleInstance = (instance: AliyunCaptchaInstance | undefined) => {
          if (!instance) {
            if (!execution.settled) {
              fail(new Error('Aliyun CAPTCHA returned no instance'));
            }

            return;
          }

          if (execution.settled) {
            safelyDestroyAliyunCaptcha(instance);
            return;
          }

          if (execution.instanceCreated) {
            // A single execution owns exactly one SDK instance. Ignore and
            // clean up any late/stale replacement rather than allowing it to
            // replace the instance whose popup was already shown.
            safelyDestroyAliyunCaptcha(instance);
            return;
          }

          const captchaMode = instance.config?.CaptchaType ?? instance.config?.captchaType;
          if (captchaMode?.toUpperCase() === 'TRACELESS') {
            fail(new Error('Aliyun CAPTCHA interactive mode is unavailable'));
            safelyDestroyAliyunCaptcha(instance);
            return;
          }

          window.clearTimeout(initTimer);
          // A non-null instance is the SDK's explicit signal that the
          // interactive CAPTCHA was initialized. Keep this separate from the
          // popup flag so early/disaster success callbacks remain fail-closed.
          // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
          execution.instanceCreated = true;
          // eslint-disable-next-line @silverhand/fp/no-mutation
          aliyunInstanceRef.current = instance;

          const completePopup = () => {
            if (execution.settled) {
              return;
            }

            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.popupShown = true;
            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.showing = false;
            const { pendingSuccess, pendingSuccessReceived } = execution;
            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.pendingSuccess = undefined;
            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.pendingSuccessReceived = false;
            if (pendingSuccessReceived) {
              succeed(pendingSuccess);
            }
          };

          try {
            // Track the call boundary so a synchronous success callback can be
            // accepted only after show() itself returns without throwing.
            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.showing = true;
            if (typeof instance.show !== 'function') {
              // The hidden trigger button is still supplied to the SDK, but a
              // token is never accepted unless this execution can explicitly
              // open its interactive popup.
              // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
              execution.showing = false;
              fail(new Error('Aliyun CAPTCHA could not be displayed'));
              return;
            }

            // Mark the popup only after show() returns. A synchronous throw
            // must not authorize the success callback.
            const showResult = instance.show();
            if (showResult && typeof showResult.then === 'function') {
              void (async () => {
                try {
                  await showResult;
                  completePopup();
                } catch {
                  // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
                  execution.showing = false;
                  fail(new Error('Aliyun CAPTCHA could not be displayed'));
                }
              })();
            } else {
              completePopup();
            }
          } catch {
            // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
            execution.showing = false;
            fail(new Error('Aliyun CAPTCHA could not be displayed'));
          }
        };

        try {
          initAliyunCaptcha({
            SceneId: captchaConfig.sceneId,
            mode: 'popup',
            element: `#${aliyunCaptchaElementId}`,
            button: `#${aliyunCaptchaButtonId}`,
            region: captchaConfig.region,
            prefix: captchaConfig.prefix,
            // Keep success deferred. Aliyun's default disaster-recovery callback
            // can otherwise race getInstance and look like a valid proof.
            delayBeforeSuccess: true,
            language: language.toLowerCase().startsWith('zh') ? 'cn' : 'en',
            slideStyle: { width: 360, height: 40 },
            success: succeed,
            // The SDK refreshes the challenge after a normal failed attempt.
            fail: () => {
              // A rejected attempt is recoverable in the same SDK popup.
            },
            getInstance: handleInstance,
            onError: () => {
              fail(new Error('Aliyun CAPTCHA SDK error'));
            },
            onClose: (reason) => {
              if (reason === 'userDismiss') {
                fail(new Error('Aliyun CAPTCHA was dismissed'));
                return;
              }

              if (execution.closeTimer === undefined) {
                // SDK versions use different close reasons and may close the popup before the
                // delayed success callback. Keep a short fail-closed window for every non-user
                // close instead of discarding a valid token that arrives immediately afterward.
                // eslint-disable-next-line @silverhand/fp/no-mutation -- local async lifecycle state.
                execution.closeTimer = window.setTimeout(() => {
                  fail(new Error('Aliyun CAPTCHA did not return a token'));
                }, aliyunCaptchaCloseGracePeriod);
              }
            },
          });
        } catch {
          fail(new Error('Aliyun CAPTCHA initialization failed'));
        }
      });
    }

    if (!window.grecaptcha?.enterprise) {
      setToast(t('error.captcha_verification_failed'));
      throw new Error('reCAPTCHA Enterprise is unavailable');
    }

    // Handle checkbox mode for reCAPTCHA Enterprise
    if (captchaConfig.mode === RecaptchaEnterpriseMode.Checkbox) {
      return new Promise<string | undefined>((resolve, reject) => {
        if (!window.grecaptcha || !widgetRef.current) {
          setToast(t('error.captcha_verification_failed'));
          reject(new Error('reCAPTCHA Enterprise is unavailable'));
          return;
        }

        // Clear the dom element first
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widgetRef.current.innerHTML = '';

        window.grecaptcha.enterprise.render(widgetRef.current, {
          sitekey: captchaConfig.siteKey,
          theme: theme === Theme.Light ? 'light' : 'dark',
          callback: (token: string) => {
            if (typeof token !== 'string' || token.trim().length === 0) {
              setToast(t('error.captcha_verification_failed'));
              reject(new Error('reCAPTCHA returned an empty token'));
              return;
            }

            resolve(token);
          },
          'error-callback': (errorCode) => {
            setToast(t('error.captcha_verification_failed'));
            reject(new Error(`reCAPTCHA error: ${errorCode}`));
          },
        });
      });
    }

    // Default invisible mode
    const token = await window.grecaptcha.enterprise.execute(captchaConfig.siteKey, {
      action: 'interaction',
    });

    if (typeof token !== 'string' || token.trim().length === 0) {
      setToast(t('error.captcha_verification_failed'));
      throw new Error('reCAPTCHA returned an empty token');
    }

    return token;
  }, [destroyCurrentAliyunCaptcha, isCaptchaRequired, captchaConfig, theme, setToast, t, language]);

  useEffect(() => {
    initCaptcha();
  }, [initCaptcha]);

  useEffect(
    () => () => {
      aliyunExecutionRef.current?.cancel();
      destroyCurrentAliyunCaptcha();
    },
    [captchaConfig, destroyCurrentAliyunCaptcha, isCaptchaRequired]
  );

  const captchaContext = useMemo<CaptchaContextType>(
    () => ({
      isCaptchaRequired,
      executeCaptcha,
      captchaConfig,
      widgetRef,
    }),
    [isCaptchaRequired, executeCaptcha, captchaConfig, widgetRef]
  );

  return <CaptchaContext.Provider value={captchaContext}>{children}</CaptchaContext.Provider>;
};

export default CaptchaContextProvider;

/* eslint-enable max-lines */
